import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Back-to-list arrow on detail/record views. Uses the in-app referrer
 * passed via React Router `location.state.from` when available so the
 * operator returns to their FILTERED list (e.g.
 * /customers?status=delinquent&search=jones), not the bare list route.
 *
 * Never uses `navigate(-1)` because browser history includes pre-app
 * pages (Google, bookmarks, new-tab) — going "back" via the browser
 * stack can pop the operator out of the app entirely.
 *
 * Pass `state={{ from: location.pathname + location.search }}` on any
 * <Link> from a list view to a detail page to enable filter
 * preservation. Without that state, falls back to the provided `to`
 * route (also safe for deep-linked loads).
 *
 * Style matches the inline-`<Link>` pattern already used by UnitDetail
 * / UnitHistory / ReservationNew so swapping those callsites
 * to this component is a visual no-op.
 */
const PageBackLink = ({ to, label }) => {
  const location = useLocation();
  // Only honor referrer when it's an in-app path (starts with "/")
  // and not the current page (avoid back-to-self loops).
  const referrer = location.state?.from;
  const target = (typeof referrer === 'string'
    && referrer.startsWith('/')
    && referrer !== location.pathname + location.search)
    ? referrer
    : to;
  return (
    <Link
      to={target}
      className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-700 mb-2"
    >
      <ArrowLeft className="h-3.5 w-3.5" />
      <span>{label}</span>
    </Link>
  );
};

export default PageBackLink;
